import React, { useState, useRef, useEffect } from 'react';

const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function pad(n) { return String(n).padStart(2, '0'); }

export default function DateTimePicker({ value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Parse current value or default to now
  const parsed = value ? new Date(value) : null;
  const [viewYear, setViewYear] = useState(parsed ? parsed.getFullYear() : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed ? parsed.getMonth() : new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(parsed ? new Date(parsed) : null);
  const [hour, setHour] = useState(parsed ? pad(parsed.getHours()) : '12');
  const [minute, setMinute] = useState(parsed ? pad(parsed.getMinutes()) : '00');

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const emitChange = (date, h, m) => {
    if (!date) return;
    const d = new Date(date);
    d.setHours(parseInt(h, 10) || 0, parseInt(m, 10) || 0, 0, 0);
    onChange(d.toISOString());
  };

  const handleDayClick = (day) => {
    const d = new Date(viewYear, viewMonth, day);
    setSelectedDate(d);
    emitChange(d, hour, minute);
  };

  const handleTimeChange = (h, m) => {
    setHour(h); setMinute(m);
    if (selectedDate) emitChange(selectedDate, h, m);
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  // Build calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const startOffset = firstDay === 0 ? 6 : firstDay - 1; // Monday-start

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isSelected = (day) => {
    if (!selectedDate || !day) return false;
    return selectedDate.getDate() === day && selectedDate.getMonth() === viewMonth && selectedDate.getFullYear() === viewYear;
  };

  const isToday = (day) => {
    if (!day) return false;
    const now = new Date();
    return now.getDate() === day && now.getMonth() === viewMonth && now.getFullYear() === viewYear;
  };

  const displayValue = parsed
    ? parsed.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';

  const s = {
    wrapper: { position: 'relative', width: '100%' },
    trigger: {
      width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 8, color: displayValue ? '#fff' : 'rgba(255,255,255,0.35)', fontSize: 13, cursor: 'pointer', textAlign: 'left',
      outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
    },
    dropdown: {
      position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50, width: 280, background: 'rgba(12,16,32,0.98)',
      border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: 14, boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
      backdropFilter: 'blur(16px)',
    },
    nav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
    navBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 16, cursor: 'pointer', padding: '4px 8px' },
    monthLabel: { fontSize: 14, fontWeight: 700, color: '#fff' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 },
    dayHeader: { textAlign: 'center', fontSize: 10, color: 'rgba(255,255,255,0.35)', padding: '4px 0', fontWeight: 700 },
    dayCell: (day, sel, today) => ({
      textAlign: 'center', padding: '7px 0', fontSize: 13, borderRadius: 6, cursor: day ? 'pointer' : 'default',
      background: sel ? '#22c55e' : today ? 'rgba(59,130,246,0.15)' : 'transparent',
      color: sel ? '#fff' : day ? 'rgba(255,255,255,0.8)' : 'transparent', fontWeight: sel || today ? 700 : 400,
      transition: 'all 0.1s', border: today && !sel ? '1px solid rgba(59,130,246,0.3)' : '1px solid transparent',
    }),
    timeRow: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, justifyContent: 'center' },
    timeInput: {
      width: 44, padding: '6px 4px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 6, color: '#fff', fontSize: 14, fontWeight: 700, textAlign: 'center', outline: 'none',
    },
  };

  return (
    <div style={s.wrapper} ref={ref}>
      <button type="button" style={s.trigger} onClick={() => setOpen(!open)}>
        {displayValue || placeholder || 'Select date & time...'}
      </button>
      {open && (
        <div style={s.dropdown}>
          <div style={s.nav}>
            <button type="button" style={s.navBtn} onClick={prevMonth}>◀</button>
            <span style={s.monthLabel}>{MONTHS[viewMonth]} {viewYear}</span>
            <button type="button" style={s.navBtn} onClick={nextMonth}>▶</button>
          </div>
          <div style={s.grid}>
            {DAYS.map((d) => <div key={d} style={s.dayHeader}>{d}</div>)}
            {cells.map((day, i) => (
              <div key={i} style={s.dayCell(day, isSelected(day), isToday(day))}
                onClick={() => day && handleDayClick(day)}
                onMouseEnter={(e) => { if (day && !isSelected(day)) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                onMouseLeave={(e) => { if (day && !isSelected(day)) e.currentTarget.style.background = isToday(day) ? 'rgba(59,130,246,0.15)' : 'transparent'; }}>
                {day || ''}
              </div>
            ))}
          </div>
          <div style={s.timeRow}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Time:</span>
            <input type="number" min="0" max="23" value={hour} onChange={(e) => handleTimeChange(e.target.value, minute)} style={s.timeInput} />
            <span style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 700 }}>:</span>
            <input type="number" min="0" max="59" step="5" value={minute} onChange={(e) => handleTimeChange(hour, e.target.value)} style={s.timeInput} />
          </div>
        </div>
      )}
    </div>
  );
}
